import Phaser from 'phaser';
import { TOWER_NAMES } from '../config/displayNames';
import { VARIETY_ORB_REFUND, rollVarietyOrb } from '../config/varietyOrb';
import type { TowerType } from '../core/types';
import type { ProgressService } from '../services/progressService';
import { saveStorage } from './saveStorage';
import { sweepAndToastAchievements } from '../ui/achievementToast';

/**
 * Opening the Variety Orb: the orb splits, a silhouette rises out of it, and
 * the tower reveals itself.
 *
 * Shared by the Level Complete claim notification (OPEN it there and then) and
 * the Storage shelf (OPEN it later), because both must show the same ceremony.
 *
 * As with `bagOpening.ts`, the roll and the grant happen BEFORE any animation:
 * closing the tab mid-reveal must not cost the player their tower, and the
 * picture can never disagree with what was actually granted.
 */

const W = 1280;
const H = 720;
/** Above the Level Complete overlay (50) and above the cutscene (500). */
const OVERLAY_DEPTH = 600;

const ORB_SIZE = 240;
const ORB_Y = H / 2 - 40;

/** Beat lengths in ms — 3s from the first pulse to the OK button. */
const PULSE_MS = 600;
const SPLIT_MS = 600;
const RISE_MS = 1000;
const REVEAL_MS = 800;

export interface OrbOpenResult {
  tower: TowerType;
  /** True when the player already had it — 500 coins were paid instead. */
  duplicate: boolean;
}

export interface OrbOpenOptions {
  progress: ProgressService;
  /** Injected for the tests/e2e; defaults to Math.random. */
  rand?: () => number;
  /** Fired after the player dismisses the reveal. */
  onDone: (result: OrbOpenResult) => void;
}

/** Tier-1 art for a tower, falling back to the Hambi Tower if it isn't loaded. */
function artKey(scene: Phaser.Scene, type: TowerType): string {
  return scene.textures.exists(`tower-${type}-1`) ? `tower-${type}-1` : 'tower-patty-1';
}

/**
 * Roll the orb, grant (or refund) it, and play the open ceremony over whatever
 * scene is showing. Returns the result immediately as well as passing it to
 * `onDone`, so a caller can update its own header without waiting.
 *
 * `progress.openOrb()` is the caller's job, not this function's: Storage has to
 * take the orb off the shelf BEFORE rolling, exactly as it takes a bag off the
 * shelf, so a double-click cannot open one orb twice.
 */
export function openVarietyOrb(scene: Phaser.Scene, opts: OrbOpenOptions): OrbOpenResult {
  const rand = opts.rand ?? Math.random;
  const tower = rollVarietyOrb(rand);

  // Grant first, animate second. `awardAnyTower` routes to whichever of the
  // three ownership stores owns this tower and is the single place ownership
  // is decided — so the orb pays out exactly once. Never pre-check ownership
  // here, or a duplicate can pay twice.
  const duplicate = !opts.progress.awardAnyTower(tower);
  if (duplicate) opts.progress.addCoins(VARIETY_ORB_REFUND);

  const result: OrbOpenResult = { tower, duplicate };

  try {
    // A new tower (or the coins from a duplicate) may have just satisfied a
    // collection or richness achievement.
    sweepAndToastAchievements(saveStorage());
  } catch (err) {
    console.warn('[varietyOrbOpening] achievement sweep failed', err);
  }

  playOrbReveal(scene, result, () => opts.onDone(result));
  return result;
}

/** The 3-second ceremony. Split out so `openVarietyOrb` reads as the rulebook. */
function playOrbReveal(scene: Phaser.Scene, result: OrbOpenResult, onClose: () => void): void {
  const objs: Phaser.GameObjects.GameObject[] = [];
  const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
    objs.push(o);
    return o;
  };

  add(
    scene.add
      .rectangle(W / 2, H / 2, W, H, 0x05030c, 0.97)
      .setDepth(OVERLAY_DEPTH)
      .setInteractive(), // swallow clicks meant for the screen underneath
  );

  // ── The orb, as two halves ────────────────────────────────────────────────
  // One texture drawn twice and cropped, rather than a second art key: the
  // halves only ever move apart, so nothing is visible that a crop can't give.
  const src = scene.textures.get('orb-variety').getSourceImage();
  const srcW = src.width;
  const srcH = src.height;
  const half = (isTop: boolean) =>
    add(
      scene.add
        .image(W / 2, ORB_Y, 'orb-variety')
        .setCrop(0, isTop ? 0 : srcH / 2, srcW, srcH / 2)
        .setDisplaySize(ORB_SIZE, ORB_SIZE)
        .setDepth(OVERLAY_DEPTH + 1),
    );
  // Crop is applied in TEXTURE space, so both halves keep the full frame and
  // stay aligned with each other; only the visible band differs.
  const top = half(true);
  const bottom = half(false);

  // ── 0.0–0.6s: the orb pulses, gathering itself ────────────────────────────
  scene.tweens.add({
    targets: [top, bottom],
    displayWidth: ORB_SIZE * 1.12,
    displayHeight: ORB_SIZE * 1.12,
    duration: PULSE_MS / 2,
    ease: 'Sine.easeInOut',
    yoyo: true,
  });

  // ── 0.6–1.2s: it cracks open ──────────────────────────────────────────────
  scene.time.delayedCall(PULSE_MS, () => {
    if (!scene.scene.isActive()) return;
    const flash = add(
      scene.add.rectangle(W / 2, H / 2, W, H, 0xffffff, 0.85).setDepth(OVERLAY_DEPTH + 4),
    );
    scene.tweens.add({ targets: flash, alpha: 0, duration: 420, ease: 'Quad.easeOut' });

    scene.tweens.add({
      targets: top,
      y: ORB_Y - 150,
      angle: -18,
      duration: SPLIT_MS,
      ease: 'Back.easeOut',
    });
    scene.tweens.add({
      targets: bottom,
      y: ORB_Y + 150,
      angle: 18,
      duration: SPLIT_MS,
      ease: 'Back.easeOut',
    });
  });

  // ── 1.2–2.2s: a blacked-out tower rises between the halves ────────────────
  scene.time.delayedCall(PULSE_MS + SPLIT_MS, () => {
    if (!scene.scene.isActive()) return;
    const art = add(
      scene.add
        .image(W / 2, ORB_Y + 40, artKey(scene, result.tower))
        .setDisplaySize(40, 40)
        .setTint(0x000000)
        .setDepth(OVERLAY_DEPTH + 2)
        .setName('orb-reveal-art'),
    );
    scene.tweens.add({
      targets: art,
      y: ORB_Y,
      displayWidth: 230,
      displayHeight: 230,
      duration: RISE_MS,
      ease: 'Back.easeOut',
    });
    // The shells fade out as the tower comes up. Left on screen they sat on
    // top of the tower name and the duplicate line; the orb has already been
    // seen to break, so there is nothing left for them to say.
    scene.tweens.add({ targets: top, alpha: 0, y: ORB_Y - 250, duration: RISE_MS });
    scene.tweens.add({ targets: bottom, alpha: 0, y: ORB_Y + 250, duration: RISE_MS });

    // ── 2.2–3.0s: the silhouette reveals itself ─────────────────────────────
    scene.time.delayedCall(RISE_MS, () => {
      if (!scene.scene.isActive()) return;
      // Tint from black to white THROUGH the sprite's own colours: clearing the
      // tint outright popped, which read as a texture swap rather than a reveal.
      scene.tweens.addCounter({
        from: 0,
        to: 1,
        duration: REVEAL_MS,
        ease: 'Quad.easeOut',
        onUpdate: (tw) => {
          const v = Math.round(0xff * (tw.getValue() ?? 1));
          art.setTint(Phaser.Display.Color.GetColor(v, v, v));
        },
        onComplete: () => art.clearTint(),
      });
      const glow = add(
        scene.add.rectangle(W / 2, H / 2, W, H, 0xffffff, 0.5).setDepth(OVERLAY_DEPTH + 4),
      );
      scene.tweens.add({ targets: glow, alpha: 0, duration: REVEAL_MS, ease: 'Quad.easeOut' });

      showOutcome(scene, result, add, onClose, objs);
    });
  });
}

/** Name, the duplicate line, and the OK button — built together, no hold. */
function showOutcome(
  scene: Phaser.Scene,
  result: OrbOpenResult,
  add: <T extends Phaser.GameObjects.GameObject>(o: T) => T,
  onClose: () => void,
  objs: Phaser.GameObjects.GameObject[],
): void {
  add(
    scene.add
      .text(W / 2, ORB_Y + 150, TOWER_NAMES[result.tower], {
        fontSize: '38px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(OVERLAY_DEPTH + 5)
      .setName('orb-reveal-name'),
  );

  add(
    scene.add
      .text(
        W / 2,
        ORB_Y + 192,
        result.duplicate
          ? `Already yours — sold for ${VARIETY_ORB_REFUND} Hambi Coins`
          : 'NEW TOWER UNLOCKED!',
        {
          fontSize: '22px',
          color: result.duplicate ? '#f5c518' : '#c77dff',
          fontStyle: 'bold',
        },
      )
      .setOrigin(0.5)
      .setDepth(OVERLAY_DEPTH + 5),
  );

  const btnY = H - 74;
  const btn = add(
    scene.add
      .rectangle(W / 2, btnY, 200, 56, 0x1a6b1a)
      .setStrokeStyle(2, 0x4caf50)
      .setInteractive({ useHandCursor: true })
      .setDepth(OVERLAY_DEPTH + 5)
      .setName('orb-reveal-ok'),
  );
  add(
    scene.add
      .text(W / 2, btnY, 'OK', { fontSize: '24px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(OVERLAY_DEPTH + 6),
  );
  btn.on('pointerup', () => {
    // Same rule as the cutscene's teardown: stop anything still tweening these
    // before they are destroyed, so nothing is left stepping a dead target.
    for (const o of objs) scene.tweens.killTweensOf(o);
    objs.forEach((o) => o.destroy());
    objs.length = 0;
    onClose();
  });
}
